import { Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { PageHeaderWrapper, Text } from "@src/appV2/redesign/system/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useEffect, useRef } from "react";

import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { invalidateShifts } from "../../invalidateShifts";
import { OpenShiftListDataProvider } from "../../Shift/Open/useOpenShiftListDataContext";
import { VirtualShiftListContextProvider } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { usePreShiftOrientationsByShiftId } from "../../Shift/PreShiftOrientation/usePreShiftOrientationsByShiftId";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { PullToRefresh } from "../ListView/PullToRefresh";
import { SHIFT_DISCOVERY_LIST_PATH, SHIFT_DISCOVERY_URGENT_PATH } from "../paths";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { UrgentShiftsList } from "./List";
import { useHandleUrgentShiftsDeepLink } from "./useHandleUrgentShiftsDeepLink";
import { useUrgentShiftsData } from "./useUrgentShiftsData";

export function ShiftDiscoveryUrgentShiftsPage() {
  const queryClient = useQueryClient();
  const stackRef = useRef<HTMLDivElement>(null);

  useHandleUrgentShiftsDeepLink();

  useScrollToTopOnStatusTap(stackRef);

  const { setBaseNavigationPathOverride } = useShiftModalsDataContext();

  /**
   * When this page is opened, we want to override the base navigation path
   * so that when shift modals are opened from here, they are rendered on top of this page
   */
  useEffect(() => {
    setBaseNavigationPathOverride(SHIFT_DISCOVERY_URGENT_PATH);
    return () => {
      setBaseNavigationPathOverride(undefined);
    };
  }, [setBaseNavigationPathOverride]);

  const {
    shifts: urgentShifts,
    isError,
    isLoading,
    isSuccess,
    workplacesMap,
    rateNegotiationsMap,
    shiftAuctionsMap,
  } = useUrgentShiftsData();

  const preShiftOrientationsByShiftId = usePreShiftOrientationsByShiftId(
    urgentShifts,
    workplacesMap
  );

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        backgroundColor: theme.palette.background.tertiary,
        overflow: "hidden",
        position: "relative",
      })}
    >
      <OpenShiftListDataProvider
        openShifts={urgentShifts}
        workplacesMap={workplacesMap}
        rateNegotiationsMap={rateNegotiationsMap}
        shiftAuctionsMap={shiftAuctionsMap}
        preShiftOrientationsByShiftId={preShiftOrientationsByShiftId}
        isOpenShiftsLoading={isLoading}
        isOpenShiftsError={isError}
        isOpenShiftsSuccess={isSuccess}
      >
        <VirtualShiftListContextProvider shifts={urgentShifts} workplacesMap={workplacesMap}>
          <PageHeaderWrapper
            sx={(theme) => ({ backgroundColor: theme.palette.background.tertiary })}
          >
            <PullToRefresh
              scrollRef={stackRef}
              onRefresh={async () => {
                await invalidateShifts(queryClient);
              }}
            />

            <PageHeaderTopRow
              title={
                <Text semibold variant="h4">
                  {urgentShifts.length > 0 ? urgentShifts.length : ""} Urgent{" "}
                  {pluralize("Shift", urgentShifts.length)}
                </Text>
              }
              fallbackGoBackPath={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`}
            />
          </PageHeaderWrapper>

          <Stack
            ref={stackRef}
            data-testid="urgent-shifts-page-scroll-container"
            spacing={7}
            sx={{
              flex: 1,
              overflow: "auto",
              paddingX: 5,
              paddingBottom: 6,
            }}
          >
            <UrgentShiftsList scrollRef={stackRef} />
          </Stack>
        </VirtualShiftListContextProvider>
      </OpenShiftListDataProvider>
    </Stack>
  );
}
